import React, { useEffect, useRef } from 'react';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

const RosterAiInsightsModal = ({
	isLoading,
	isOpen,
	onClose,
	rosterAiInsights
}) => {
	const modalRef = useRef(null);

	const handleClickOutsideModal = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			onClose();
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutsideModal);
		return () => {
			document.removeEventListener('mousedown', handleClickOutsideModal);
		};
	}, []);

	// Prevent background scrolling when the modal is open
	useEffect(
		() => {
			if (isOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		},
		[
			isOpen
		]
	);

	const getGradeColor = (grade) => {
		const letter =
			grade && grade.charAt(0) ? grade.charAt(0).toUpperCase() : null;
		switch (letter) {
			case 'A':
				return 'text-green-700 bg-green-50 border-green-300 dark:text-green-300 dark:bg-green-900 dark:bg-opacity-30 dark:border-green-700';
			case 'B':
				return 'text-blue-700 bg-blue-50 border-blue-300 dark:text-blue-300 dark:bg-blue-900 dark:bg-opacity-30 dark:border-blue-700';
			case 'C':
				return 'text-yellow-700 bg-yellow-50 border-yellow-300 dark:text-yellow-300 dark:bg-yellow-900 dark:bg-opacity-30 dark:border-yellow-700';
			case 'D':
				return 'text-yellow-800 bg-yellow-100 border-yellow-500 dark:text-yellow-200 dark:bg-yellow-800 dark:bg-opacity-30 dark:border-yellow-600';
			case 'F':
				return 'text-red-700 bg-red-50 border-red-300 dark:text-red-300 dark:bg-red-900 dark:bg-opacity-30 dark:border-red-700';
			default:
				return 'text-gray-700 bg-gray-50 border-gray-300 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-600';
		}
	};

	// Professional SVG Icons
	const ClipboardIcon = () => (
		<svg
			className="w-6 h-6"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
			/>
		</svg>
	);

	const ShieldIcon = ({ isPositive }) => (
		<svg
			className="w-6 h-6"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d={
					isPositive ? (
						'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
					) : (
						'M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01'
					)
				}
			/>
		</svg>
	);

	const TargetIcon = () => (
		<svg
			className="w-6 h-6"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
			/>
		</svg>
	);

	const TrendingIcon = () => (
		<svg
			className="w-6 h-6"
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
			/>
		</svg>
	);

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60"
			style={{ zIndex: 1000 }}
		>
			<div
				className="relative bg-white dark:bg-dark-card rounded-2xl shadow-2xl text-gray-800 dark:text-dark-text w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden border border-gray-200 dark:border-dark-border"
				ref={modalRef}
			>
				{/* Header */}
				<div className="bg-ff-black text-white px-8 py-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-4">
							<div className="flex items-center justify-center w-12 h-12 bg-white bg-opacity-10 rounded-lg">
								<svg
									className="w-7 h-7 text-white"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
									/>
								</svg>
							</div>
							<div>
								<h2 className="text-2xl font-bold tracking-tight">
									AI Roster Evaluation
								</h2>
								<p className="text-gray-300 text-sm mt-0.5">
									Strategic Analysis & Recommendations
								</p>
							</div>
						</div>
						<button
							className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white hover:bg-opacity-10 transition-all duration-200"
							onClick={onClose}
						>
							<svg
								className="h-6 w-6"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>

				{/* Content */}
				<div
					className="overflow-y-auto p-8 bg-gray-50 dark:bg-dark-bg"
					style={{ maxHeight: 'calc(90vh - 120px)' }}
				>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<LoadingSpinner />
						</div>
					) : rosterAiInsights ? (
						<div className="space-y-6">
							{/* Executive Summary */}
							{rosterAiInsights.executive_summary && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="bg-ff-black bg-opacity-5 dark:bg-white dark:bg-opacity-5 px-6 py-4 border-b border-gray-200 dark:border-dark-border">
										<div className="flex items-center">
											<div className="text-ff-black dark:text-dark-text mr-3">
												<ClipboardIcon />
											</div>
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text tracking-tight">
												Executive Summary
											</h3>
										</div>
									</div>
									<div className="p-6">
										<div className="space-y-4">
											{rosterAiInsights.executive_summary.overall_grade && (
												<div className="flex items-center justify-between py-2">
													<span className="text-gray-700 dark:text-gray-300 font-semibold">
														Overall Grade:
													</span>
													<div
														className={`px-5 py-2.5 rounded-lg border-2 font-bold text-xl ${getGradeColor(
															rosterAiInsights.executive_summary.overall_grade
														)}`}
													>
														{rosterAiInsights.executive_summary.overall_grade}
													</div>
												</div>
											)}
											{rosterAiInsights.executive_summary.grade_rationale && (
												<div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
														{rosterAiInsights.executive_summary.grade_rationale}
													</p>
												</div>
											)}
											{rosterAiInsights.executive_summary.roster_identity && (
												<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
													<p className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-2">
														Roster Identity
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed">
														{rosterAiInsights.executive_summary.roster_identity}
													</p>
												</div>
											)}
										</div>
									</div>
								</div>
							)}

							{/* Roster Assessment */}
							{rosterAiInsights.roster_assessment && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="bg-ff-black bg-opacity-5 dark:bg-white dark:bg-opacity-5 px-6 py-4 border-b border-gray-200 dark:border-dark-border">
										<div className="flex items-center">
											<div className="text-ff-black dark:text-dark-text mr-3">
												<ShieldIcon isPositive={true} />
											</div>
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text tracking-tight">
												Roster Assessment
											</h3>
										</div>
									</div>
									<div className="p-6">
										{/* Core Strengths */}
										{rosterAiInsights.roster_assessment.core_strengths &&
										rosterAiInsights.roster_assessment.core_strengths.length >
											0 && (
											<div className="mb-6">
												<h4 className="text-xs font-bold text-green-700 dark:text-green-300 uppercase tracking-wider mb-3">
													Core Strengths
												</h4>
												<ul className="space-y-2.5">
													{rosterAiInsights.roster_assessment.core_strengths.map(
														(strength, index) => (
															<li
																key={index}
																className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 border-l-4 border-green-500 pl-4 py-3 rounded-r-md hover:bg-green-100 dark:hover:bg-green-900 dark:hover:bg-opacity-30 transition-colors"
															>
																<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
																	{strength}
																</p>
															</li>
														)
													)}
												</ul>
											</div>
										)}

										{/* Critical Vulnerabilities */}
										{rosterAiInsights.roster_assessment
											.critical_vulnerabilities &&
										rosterAiInsights.roster_assessment.critical_vulnerabilities
											.length > 0 && (
											<div>
												<h4 className="text-xs font-bold text-red-700 dark:text-red-300 uppercase tracking-wider mb-3">
													Critical Vulnerabilities
												</h4>
												<ul className="space-y-2.5">
													{rosterAiInsights.roster_assessment.critical_vulnerabilities.map(
														(vulnerability, index) => (
															<li
																key={index}
																className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 border-l-4 border-red-500 pl-4 py-3 rounded-r-md hover:bg-red-100 dark:hover:bg-red-900 dark:hover:bg-opacity-30 transition-colors"
															>
																<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
																	{vulnerability}
																</p>
															</li>
														)
													)}
												</ul>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Action Plan */}
							{rosterAiInsights.action_plan && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="bg-ff-black bg-opacity-5 dark:bg-white dark:bg-opacity-5 px-6 py-4 border-b border-gray-200 dark:border-dark-border">
										<div className="flex items-center">
											<div className="text-ff-black dark:text-dark-text mr-3">
												<TargetIcon />
											</div>
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text tracking-tight">
												Action Plan
											</h3>
										</div>
									</div>
									<div className="p-6">
										<div className="space-y-3">
											{rosterAiInsights.action_plan.priority_one && (
												<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border-l-4 border-blue-600 pl-4 py-3.5 rounded-r-md">
													<div className="flex items-center mb-2">
														<span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded uppercase tracking-wide">
															Priority 1
														</span>
													</div>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm font-medium">
														{rosterAiInsights.action_plan.priority_one}
													</p>
												</div>
											)}
											{rosterAiInsights.action_plan.priority_two && (
												<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border-l-4 border-blue-500 pl-4 py-3.5 rounded-r-md">
													<div className="flex items-center mb-2">
														<span className="bg-blue-500 text-white text-xs font-bold px-2.5 py-1 rounded uppercase tracking-wide">
															Priority 2
														</span>
													</div>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
														{rosterAiInsights.action_plan.priority_two}
													</p>
												</div>
											)}
											{rosterAiInsights.action_plan.priority_three && (
												<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border-l-4 border-blue-400 pl-4 py-3.5 rounded-r-md">
													<div className="flex items-center mb-2">
														<span className="bg-blue-400 text-white text-xs font-bold px-2.5 py-1 rounded uppercase tracking-wide">
															Priority 3
														</span>
													</div>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
														{rosterAiInsights.action_plan.priority_three}
													</p>
												</div>
											)}
											{rosterAiInsights.action_plan.additional_considerations &&
											rosterAiInsights.action_plan.additional_considerations
												.length > 0 && (
												<div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
													<h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
														Additional Considerations
													</h4>
													<ul className="space-y-2">
														{rosterAiInsights.action_plan.additional_considerations.map(
															(consideration, index) => (
																<li
																	key={index}
																	className="bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2.5 rounded-r-md text-sm"
																>
																	<p className="text-gray-700 dark:text-gray-300 leading-relaxed">
																		{consideration}
																	</p>
																</li>
															)
														)}
													</ul>
												</div>
											)}
										</div>
									</div>
								</div>
							)}

							{/* Outlook */}
							{rosterAiInsights.outlook && (
								<div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
									<div className="bg-ff-black bg-opacity-5 dark:bg-white dark:bg-opacity-5 px-6 py-4 border-b border-gray-200 dark:border-dark-border">
										<div className="flex items-center">
											<div className="text-ff-black dark:text-dark-text mr-3">
												<TrendingIcon />
											</div>
											<h3 className="text-lg font-bold text-gray-900 dark:text-dark-text tracking-tight">
												Outlook & Conclusion
											</h3>
										</div>
									</div>
									<div className="p-6">
										<div className="space-y-3.5">
											{rosterAiInsights.outlook.competitive_window && (
												<div className="bg-gray-50 dark:bg-gray-900 dark:bg-opacity-30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
													<p className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
														Competitive Window
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
														{rosterAiInsights.outlook.competitive_window}
													</p>
												</div>
											)}
											{rosterAiInsights.outlook.championship_probability && (
												<div className="bg-yellow-50 dark:bg-yellow-900 dark:bg-opacity-20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
													<p className="text-xs font-bold text-yellow-700 dark:text-yellow-300 uppercase tracking-wider mb-2">
														Championship Probability
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
														{rosterAiInsights.outlook.championship_probability}
													</p>
												</div>
											)}
											{rosterAiInsights.outlook.risk_factors && (
												<div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-20 p-4 rounded-lg border border-red-200 dark:border-red-800">
													<p className="text-xs font-bold text-red-700 dark:text-red-300 uppercase tracking-wider mb-2">
														Risk Factors
													</p>
													<p className="text-gray-800 dark:text-gray-200 leading-relaxed text-sm">
														{rosterAiInsights.outlook.risk_factors}
													</p>
												</div>
											)}
											{rosterAiInsights.outlook.final_verdict && (
												<div className="bg-ff-black bg-opacity-5 dark:bg-white dark:bg-opacity-5 p-5 rounded-lg border-2 border-gray-300 dark:border-gray-600">
													<p className="text-xs font-bold text-gray-900 dark:text-dark-text uppercase tracking-wider mb-2">
														Final Verdict
													</p>
													<p className="text-gray-900 dark:text-gray-100 leading-relaxed font-semibold text-sm">
														{rosterAiInsights.outlook.final_verdict}
													</p>
												</div>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					) : (
						<div className="text-center py-12">
							<p className="text-gray-500 dark:text-gray-400 text-lg">No analysis available.</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default RosterAiInsightsModal;
